// src/views/admin/ADConfigCartes.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";

function Modal({ title, onClose, children, maxWidth = 480 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const EMPTY = {
  annee_scolaire: "",
  numero_ecole: "",
  description: "",
  actif: true,
};

export default function ADConfigCartes({ slug }) {
  const [annees, setAnnees] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [modal, setModal] = useState(null); // create | edit | delete
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const toast = useToastHook();
  const {
    getAnneesScolaires,
    getConfigurationsCarte, createConfigurationCarte,
    updateConfigurationCarte, updateConfigurationCartePartial,
    deleteConfigurationCarte,
  } = useAdminService();

  useEffect(() => { if (slug) init(); }, [slug]);

  const init = async () => {
    setLoading(true);
    try {
      const [anneesData, configsData] = await Promise.all([
        getAnneesScolaires(slug, { show_archived: "false" }),
        getConfigurationsCarte(slug),
      ]);
      setAnnees(anneesData);
      setConfigs(configsData);
    } catch (error) {
      toast.error("Impossible de charger les configurations de carte");
    } finally {
      setLoading(false);
    }
  };

  const loadConfigs = async () => {
    try {
      const data = await getConfigurationsCarte(slug);
      setConfigs(data);
    } catch (error) {
      toast.error("Impossible de charger les configurations de carte");
    }
  };

  // ── CRUD ──────────────────────────────────────────────────────────
  const openCreate = () => {
    const anneeActive = annees.find(a => a.actif);
    setForm({ ...EMPTY, annee_scolaire: String((anneeActive || annees[0])?.id || "") });
    setModal("create");
  };

  const openEdit = (c) => {
    setSelected(c);
    setForm({
      annee_scolaire: c.annee_scolaire || "",
      numero_ecole: c.numero_ecole || "",
      description: c.description || "",
      actif: c.actif,
    });
    setModal("edit");
  };

  const openDelete = (c) => { setSelected(c); setModal("delete"); };

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.annee_scolaire) { toast.error("L'année scolaire est requise"); return; }

    setSaving(true);
    try {
      if (modal === "create") {
        const created = await createConfigurationCarte(slug, form);
        setConfigs(prev => [created, ...prev]);
        toast.success("Configuration créée avec succès");
      } else {
        const updated = await updateConfigurationCarte(slug, selected.id, form);
        setConfigs(prev => prev.map(c => c.id === selected.id ? updated : c));
        toast.success("Configuration modifiée avec succès");
      }
      setModal(null);
      await loadConfigs(); // recharge pour refléter la désactivation des autres configs
    } catch (error) {
      const data = error.response?.data;
      toast.error(typeof data === "object" ? Object.values(data).flat().join("\n") : "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteConfigurationCarte(slug, selected.id);
      setConfigs(prev => prev.filter(c => c.id !== selected.id));
      toast.success("Configuration supprimée avec succès");
      setModal(null);
    } catch (error) {
      toast.error("Impossible de supprimer cette configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActif = async (c) => {
    if (c.actif) return; // une seule config active à la fois, globale à l'établissement
    try {
      await updateConfigurationCartePartial(slug, c.id, { actif: true });
      await loadConfigs(); // le backend désactive les autres, on recharge pour rester cohérent
      toast.success("Configuration activée");
    } catch (error) {
      toast.error("Impossible d'activer cette configuration");
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <div style={{ flex: 1 }} />
        <button className="bsec" onClick={loadConfigs}><i className="ti ti-refresh" />Actualiser</button>
        <button className="bprim" onClick={openCreate}>
          <i className="ti ti-plus" />Nouvelle configuration
        </button>
      </div>

      <div style={{ fontSize: 13, color: "var(--h)", marginTop: 8 }}>
        Une seule configuration peut être active à la fois pour l'établissement (toutes années confondues).
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 10 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Année scolaire</th>
              <th>N° établissement</th>
              <th>Description</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {configs.map(c => (
              <tr key={c.id}>
                <td style={{ fontWeight: 500 }}>{c.annee_scolaire_nom}</td>
                <td>{c.numero_ecole || "—"}</td>
                <td style={{ fontSize: 13, color: "var(--m)" }}>{c.description || "—"}</td>
                <td>
                  {c.actif ? (
                    <span className="pill ok">Active</span>
                  ) : (
                    <button className="pill neu" style={{ border: "none", cursor: "pointer" }} onClick={() => handleToggleActif(c)}>
                      Inactive — activer
                    </button>
                  )}
                </td>
                <td>
                  <div className="act">
                    <button title="Modifier" onClick={() => openEdit(c)}><i className="ti ti-edit" /></button>
                    <button className="del" title="Supprimer" onClick={() => openDelete(c)}><i className="ti ti-trash" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {configs.length === 0 && (
              <tr><td colSpan="5" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
                Aucune configuration trouvée
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Créer/Modifier */}
      {(modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouvelle configuration de carte" : "Modifier la configuration"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Année scolaire *</label>
              <select value={form.annee_scolaire} onChange={e => handleFormChange('annee_scolaire', e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {annees.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
            </div>
            <div className="fg">
              <label>Numéro d'établissement</label>
              <input value={form.numero_ecole} onChange={e => handleFormChange('numero_ecole', e.target.value)} />
            </div>
            <div className="fg">
              <label>Description</label>
              <textarea value={form.description} onChange={e => handleFormChange('description', e.target.value)} rows={3} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={form.actif} onChange={e => handleFormChange('actif', e.target.checked)} />
              Configuration active
              <span style={{ color: "var(--m)", fontSize: 12 }}>(désactive automatiquement les autres configurations)</span>
            </label>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Supprimer */}
      {modal === "delete" && selected && (
        <Modal title="Confirmer la suppression" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer la configuration <strong>{selected.annee_scolaire_nom}</strong> ?
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}